
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    Table
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PermissionsTableBody from './PermissionsTableBody';

interface FormValues {
    name: string;
    permissions: any; // Object to hold checked state
}

export default function AddRole() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [permissions, setPermissions] = useState<any>({}); // Grouped by module
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [permissionError, setPermissionError] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const response: any = await apiPost("roles/permissions", {});
            setPermissions(response);
        } catch (error) {
            console.error(error);
        } finally {
            setPermissionsLoading(false);
        }
    };

    const handleCheckboxChange = (permId: number) => {
        setSelectedPermissions(prev => {
            const next = prev.includes(permId)
                ? prev.filter(id => id !== permId)
                : [...prev, permId];
            if (next.length > 0) setPermissionError(false); // clear error once something is selected
            return next;
        });
    };

    const handleSelectAll = (moduleName: string, isChecked: boolean) => {
        const modulePerms = permissions[moduleName];
        if (!modulePerms) return;
        const modulePermIds = modulePerms.map((p: any) => p.id);
        setSelectedPermissions(prev => {
            let next = [...prev];
            if (isChecked) {
                modulePermIds.forEach((id: number) => { if (!next.includes(id)) next.push(id); });
            } else {
                next = next.filter(id => !modulePermIds.includes(id));
            }
            if (next.length > 0) setPermissionError(false);
            return next;
        });
    };

    const onSubmit = async (data: FormValues) => {
        // Validation: At least one permission must be selected
        if (selectedPermissions.length === 0) {
            setPermissionError(true);
            // Scroll to the table so user can see the highlight
            document.getElementById('permissions-matrix-table')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setPermissionError(false);

        setLoading(true);

        // Validation: Check if View is selected for dependent permissions
        for (const moduleName of Object.keys(permissions)) {
            const viewId = getPermId(moduleName, 'view');
            const hasView = viewId && selectedPermissions.includes(viewId);

            const dependentActions = ['add', 'edit', 'delete', 'transfer', 'approve', 'download', 'print', 'invoice'];

            for (const action of dependentActions) {
                const permId = getPermId(moduleName, action);
                if (permId && selectedPermissions.includes(permId) && !hasView) {
                    toast.error(`View permission is required for ${moduleName} module`);
                    setLoading(false);
                    return;
                }
            }
        }

        try {
            const payload = {
                name: data.name,
                permissions: selectedPermissions
            };
            await apiPost("roles/store", payload);
            toast.success("Role added successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/role`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding role");
        } finally {
            setLoading(false);
        }
    };

    // Helper to get permission ID for a module+action
    const getPermId = (moduleName: string, action: string) => {
        const perms = permissions[moduleName];
        if (!perms) return null;
        const p = perms.find((x: any) => x.action === action);
        return p ? p.id : null;
    };



    // Check if all available permissions for a module are selected
    const isModuleAllSelected = (moduleName: string) => {
        const modulePerms = permissions[moduleName];
        if (!modulePerms || modulePerms.length === 0) return false;
        return modulePerms.every((p: any) => selectedPermissions.includes(p.id));
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Role" parent="Master Settings" title="Add Role" />
            <Container fluid>
                <Card className="shadow-sm">

                    {permissionsLoading ? (
                        <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div>
                    ) : (
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Label className="fw-bold">Role Name <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: "Role Name is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Enter Role Name " invalid={!!errors.name} autoComplete="off" />
                                                    <FormFeedback>{errors.name?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                                    <H5>Permissions Matrix <span className="text-danger">*</span></H5>
                                    <small className="text-muted">Select permissions for this role</small>
                                </div>

                                <div
                                    id="permissions-matrix-table"
                                    className="table-responsive"
                                    style={permissionError ? {
                                        border: '2px solid #dc3545',
                                        borderRadius: '6px',
                                        boxShadow: '0 0 0 3px rgba(220,53,69,0.15)',
                                        transition: 'box-shadow 0.2s ease'
                                    } : { border: '2px solid transparent', borderRadius: '6px', transition: 'box-shadow 0.2s ease' }}
                                >
                                    <Table bordered hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '20%' }} className="text-dark">Module</th>
                                                <th className="text-center text-dark">View</th>
                                                <th className="text-center text-dark">Add</th>
                                                <th className="text-center text-dark">Edit</th>
                                                <th className="text-center text-dark">Delete</th>
                                                <th className="text-center text-dark">Transfer</th>
                                                <th className="text-center text-dark">Approve</th>
                                                <th className="text-center text-dark">Download</th>
                                                <th className="text-center text-dark">Print</th>
                                                <th className="text-center text-dark">Invoice</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <PermissionsTableBody
                                                permissions={permissions}
                                                selectedPermissions={selectedPermissions}
                                                onCheckboxChange={handleCheckboxChange}
                                                onSelectAll={handleSelectAll}
                                                isModuleAllSelected={isModuleAllSelected}
                                            />
                                        </tbody>
                                    </Table>
                                </div>
                                {permissionError && (
                                    <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                        Please select at least one permission for this role.
                                    </div>
                                )}


                                <div className="mt-4 d-flex justify-content-start gap-2">
                                    <Btn
                                        color="primary"
                                        disabled={loading}
                                        type="submit"
                                        onClick={() => {
                                            // Eagerly show permission error even if RHF blocks submit due to other fields
                                            if (selectedPermissions.length === 0) {
                                                setPermissionError(true);
                                                document.getElementById('permissions-matrix-table')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }
                                        }}
                                    >
                                        {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                    </Btn>
                                </div>
                            </Form>
                        </CardBody>
                    )}
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
